import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete from '@mui/material/Autocomplete';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const ruleLabels = { bogo_free: "Buy X, Get Y Free", bogo_percent: "Buy X, Get Y at % Off", bulk_percent: "Buy N or More, % Off Entire Line" };

const OfferForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [descriptionZh, setDescriptionZh] = useState("");
    const [ruleType, setRuleType] = useState("bogo_free");
    const [buyQty, setBuyQty] = useState(2);
    const [freeQty, setFreeQty] = useState(1);
    const [discountQty, setDiscountQty] = useState(1);
    const [discountPercent, setDiscountPercent] = useState(50);
    const [minQty, setMinQty] = useState(3);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [active, setActive] = useState(true);

    const [selectedProducts, setSelectedProducts] = useState([]);
    const [allProducts, setAllProducts] = useState([]);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/products');
                setAllProducts(data.products);
            } catch (err) {
                console.error('Failed to load products', err);
            }
        };
        fetchProducts();
    }, []);

    useEffect(() => {
        if (!isEditMode) {
            setLoading(false);
            return;
        }
        const fetchOffer = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/offers');
                const offer = data.offers.find((o) => o._id === id);
                if (!offer) {
                    enqueueSnackbar("Offer not found", { variant: "error" });
                    navigate('/admin/offers');
                    return;
                }
                setName(offer.name);
                setDescription(offer.description || "");
                setDescriptionZh(offer.descriptionZh || "");
                setRuleType(offer.ruleType);
                setBuyQty(offer.buyQty ?? 2);
                setFreeQty(offer.freeQty ?? 1);
                setDiscountQty(offer.discountQty ?? 1);
                setDiscountPercent(offer.discountPercent ?? 50);
                setMinQty(offer.minQty ?? 3);
                setStartDate(offer.startDate ? new Date(offer.startDate).toISOString().substring(0, 10) : "");
                setEndDate(offer.endDate ? new Date(offer.endDate).toISOString().substring(0, 10) : "");
                setActive(offer.active);
                setSelectedProducts(offer.products || []);
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load offer", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchOffer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!name.trim()) return enqueueSnackbar("Please enter a name", { variant: "warning" });
        if (selectedProducts.length === 0) return enqueueSnackbar("Please select at least one product", { variant: "warning" });

        const payload = {
            name, description, descriptionZh, ruleType, active,
            products: selectedProducts.map((p) => p._id),
            startDate: startDate || undefined,
            endDate: endDate || undefined,
        };
        if (ruleType === "bogo_free") {
            payload.buyQty = buyQty;
            payload.freeQty = freeQty;
        } else if (ruleType === "bogo_percent") {
            payload.buyQty = buyQty;
            payload.discountQty = discountQty;
            payload.discountPercent = discountPercent;
        } else if (ruleType === "bulk_percent") {
            payload.minQty = minQty;
            payload.discountPercent = discountPercent;
        }

        setSaving(true);
        try {
            if (isEditMode) {
                await axios.put(`/api/v1/admin/offer/${id}`, payload);
                enqueueSnackbar("Offer updated", { variant: "success" });
            } else {
                await axios.post('/api/v1/admin/offer/new', payload);
                enqueueSnackbar("Offer created", { variant: "success" });
            }
            navigate('/admin/offers');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save offer", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: ${isEditMode ? "Edit" : "New"} Offer | 1313 Mart`} />
            {saving && <BackdropLoader />}

            <form onSubmit={submitHandler} className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6" id="offerform">

                <h1 className="text-lg font-medium uppercase">{isEditMode ? "Edit" : "New"} Offer</h1>

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField label="Internal Name" variant="outlined" size="small" required fullWidth value={name} onChange={(e) => setName(e.target.value)} helperText="For your reference in the admin panel" />
                    <TextField label="Customer-Facing Description (optional)" variant="outlined" size="small" fullWidth value={description} onChange={(e) => setDescription(e.target.value)} helperText="Leave blank to auto-generate one" />
                    <TextField label="Description (中文 — optional)" variant="outlined" size="small" fullWidth value={descriptionZh} onChange={(e) => setDescriptionZh(e.target.value)} />
                </div>

                <TextField label="Rule Type" select variant="outlined" size="small" value={ruleType} onChange={(e) => setRuleType(e.target.value)} sx={{ maxWidth: 320 }}>
                    {Object.entries(ruleLabels).map(([key, label]) => (
                        <MenuItem value={key} key={key}>{label}</MenuItem>
                    ))}
                </TextField>

                {ruleType === "bogo_free" && (
                    <div className="flex gap-4">
                        <TextField label="Buy Quantity" type="number" variant="outlined" size="small" value={buyQty} onChange={(e) => setBuyQty(Number(e.target.value))} sx={{ maxWidth: 160 }} />
                        <TextField label="Free Quantity" type="number" variant="outlined" size="small" value={freeQty} onChange={(e) => setFreeQty(Number(e.target.value))} sx={{ maxWidth: 160 }} />
                    </div>
                )}

                {ruleType === "bogo_percent" && (
                    <div className="flex gap-4">
                        <TextField label="Buy Quantity" type="number" variant="outlined" size="small" value={buyQty} onChange={(e) => setBuyQty(Number(e.target.value))} sx={{ maxWidth: 160 }} />
                        <TextField label="Discounted Quantity" type="number" variant="outlined" size="small" value={discountQty} onChange={(e) => setDiscountQty(Number(e.target.value))} sx={{ maxWidth: 160 }} />
                        <TextField label="Discount %" type="number" variant="outlined" size="small" value={discountPercent} onChange={(e) => setDiscountPercent(Number(e.target.value))} sx={{ maxWidth: 160 }} />
                    </div>
                )}

                {ruleType === "bulk_percent" && (
                    <div className="flex gap-4">
                        <TextField label="Minimum Quantity" type="number" variant="outlined" size="small" value={minQty} onChange={(e) => setMinQty(Number(e.target.value))} sx={{ maxWidth: 160 }} />
                        <TextField label="Discount %" type="number" variant="outlined" size="small" value={discountPercent} onChange={(e) => setDiscountPercent(Number(e.target.value))} sx={{ maxWidth: 160 }} />
                    </div>
                )}

                <Autocomplete
                    multiple
                    size="small"
                    options={allProducts}
                    getOptionLabel={(opt) => opt.name || ""}
                    isOptionEqualToValue={(opt, val) => opt._id === val._id}
                    value={selectedProducts}
                    onChange={(e, newValue) => setSelectedProducts(newValue)}
                    renderInput={(params) => <TextField {...params} label="Applies To" placeholder="Search products..." />}
                />

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField label="Start Date (optional)" type="date" variant="outlined" size="small" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={{ maxWidth: 220 }} />
                    <TextField label="End Date (optional)" type="date" variant="outlined" size="small" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={{ maxWidth: 220 }} />
                </div>

                <FormControlLabel
                    control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />}
                    label="Active"
                />

                <div className="flex justify-end">
                    <input form="offerform" type="submit" className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg cursor-pointer" value={isEditMode ? "Save Changes" : "Create Offer"} />
                </div>

            </form>
        </>
    );
};

export default OfferForm;
